import classNames from 'classnames';
import {format} from 'date-fns/format';
import React, {DependencyList, FC, useCallback} from 'react';
import {getOrderResultSign, TransactionSchema, TransactionTypeName} from '~/entities/transactions-history';
import arrowRight from '../imgs/arrow-right.svg';
import scss from './TransactionMain.module.scss';

export interface TransactionMainProps {
    details: TransactionSchema;
}

const TransactionMain: FC<TransactionMainProps> = ({details}) => {
    const sign = getOrderResultSign(details);

    const volume = details.volume;
    const displayedValue = `${volume.value ? sign : ''}${volume.value || '−−'} ${volume.currency}`;

    const fontAdjustingRef = useFontAdjusting([displayedValue]);

    return (
        <div className={scss.container}>
            <div className={scss.info}>
                {shouldShowFlowArrow(details.type) && (
                    <div className={scss.currencies}>
                        <div>{details.flow.from}</div>
                        <img src={arrowRight} className={scss.arrow} />
                        <div>{details.flow.to}</div>
                    </div>
                )}

                <div
                    ref={fontAdjustingRef}
                    className={classNames(scss.volumeValue, {
                        [scss.complete]: details.status === 'completed',
                        [scss.positive]: sign === '+',
                        [scss.negative]: sign !== '+',
                    })}
                >
                    {displayedValue}
                </div>

                <div className={scss.date}>{format(details.created_at, 'd MMMM yyyy, HH:mm')}</div>
            </div>
        </div>
    );
};

export default TransactionMain;

function shouldShowFlowArrow(type: TransactionTypeName) {
    // yep, just hardcoding this stuff
    const typesWithoutFlowArrow: TransactionTypeName[] = [
        'top_up_correction',
        'top_up_payment',
        'send_p2p',
        'receive_p2p',
        'withdraw_fee',
        'referral_bonus',
        'send',
        'receive',
        'token_send',
        'token_receive',
        'token_migrate',
        'token_refund',
        'token_redemption',
        'token_instant_redemption',
    ];
    return !typesWithoutFlowArrow.includes(type);
}

/**
 * Could not find an easy css solution for this,
 * initial guess that `font-size: calc(1em - (var(--value-length) * var(--some-magic-coefficient)));` would work — was wrong.
 * As font scales down so does the width added by each character, making --some-magic-coefficient not a constant but
 * some kind of non-linearly adjusting value, calculating it was way to difficult
 */
const useFontAdjusting = (deps: DependencyList) =>
    useCallback((el: HTMLElement | null) => {
        if (!el) return;

        const computed = window.getComputedStyle(el);
        let fontSize = parseFloat(computed.fontSize);
        let lineHeight = parseFloat(computed.lineHeight);
        const step = 2;

        while (el.scrollWidth > el.clientWidth) {
            el.style.fontSize = `${(fontSize -= step)}px`;
            el.style.lineHeight = `${(lineHeight -= step)}px`;
        }
    }, deps);
